import { OverwatchActionFailure, OverwatchActionsState } from 'api/overwatch';

import {
  IOverwatchActionDefinition,
  OverwatchActionKey,
  OverwatchActionOriginType
} from '../widgets/types';

export const CREATE_SUPPORT_TICKET: OverwatchActionKey = 'create_support_ticket';
export const SCHEDULE_RENEWAL_REVIEW: OverwatchActionKey = 'schedule_renewal_review';
export const BOOK_A_DEMO: OverwatchActionKey = 'book_a_demo';

export const ACTION_COPY = {
  notAvailable: 'Not available yet',
  sending: 'Sending…',
  requestSent: 'Request sent',
  sentOn: (date: string) => `Sent on ${date}`,
  forbidden: "You don't have permission to send this request.",
  unknownAction: 'This request is not available.',
  invalid: 'This request could not be sent. Check the details and try again.',
  inFlight: 'This request is still being processed. Try again in a moment.',
  notConfigured: 'Support requests are not configured for this tenant.',
  rateLimited: 'Support is receiving too many requests. Try again later.',
  rateLimitedIn: (seconds: number) =>
    `Support is receiving too many requests. Try again in ${seconds} s.`,
  rateLimitedOver: 'Support is receiving too many requests. Try again now.',
  upstreamFailed: 'The support desk did not respond. Try again.',
  rejected: 'The support desk rejected this request.',
  sourceUnavailable: 'This request cannot be checked right now. Try again.',
  network: 'Network error. Check your connection and try again.',
  generic: 'Something went wrong. Try again later!'
};

/** A 50332 reads as a countdown while its wait runs, then as an invitation to retry. */
export const failureMessage = (
  failure: Pick<IActionFailureView, 'message'>,
  retryAt: number | null,
  secondsLeft: number
): string => {
  if (retryAt === null) return failure.message;

  return secondsLeft > 0 ? ACTION_COPY.rateLimitedIn(secondsLeft) : ACTION_COPY.rateLimitedOver;
};

export type OverwatchActionAvailability = 'enabled' | 'disabled' | 'hidden';

/** Discovery leaves the `origin` input out when the caller may use no origin type. */
export const originTypesOf = (action?: IOverwatchActionDefinition): OverwatchActionOriginType[] =>
  action?.inputs?.origin?.types ?? [];

/**
 * Listed in discovery: enabled. Discovery answered without it (or without the origin type): hidden.
 * Discovery still loading or unavailable: drawn, but disabled.
 */
export const actionAvailability = (
  state: OverwatchActionsState,
  key: OverwatchActionKey,
  origin?: OverwatchActionOriginType
): OverwatchActionAvailability => {
  if (state.status !== 'ready') return 'disabled';

  const action = state.actions[key];

  if (!action) return 'hidden';

  return !origin || originTypesOf(action).includes(origin) ? 'enabled' : 'hidden';
};

export interface IActionFailureView {
  message: string;
  /** A retry resends the same body (with a fresh id after a 40931). */
  retryable: boolean;
  retryAfterMs: number | null;
  /** A 4221's first invalid field, such as `input.title`. */
  field: string | null;
}

const view = (
  message: string,
  retryable: boolean,
  field: string | null = null
): IActionFailureView => ({
  message,
  retryable,
  retryAfterMs: null,
  field
});

export const actionFailureView = (failure: OverwatchActionFailure): IActionFailureView => {
  switch (failure.kind) {
    case 'forbidden':
      return view(ACTION_COPY.forbidden, false);
    case 'unknownAction':
      return view(ACTION_COPY.unknownAction, false);
    case 'invalid':
      return view(failure.message ?? ACTION_COPY.invalid, false, failure.field);
    case 'idConflict':
      return view(ACTION_COPY.generic, true);
    case 'inFlight':
      return view(ACTION_COPY.inFlight, true);
    case 'notConfigured':
      return view(ACTION_COPY.notConfigured, false);
    case 'rateLimited':
      return {
        message:
          failure.retryAfterMs === null
            ? ACTION_COPY.rateLimited
            : ACTION_COPY.rateLimitedIn(Math.ceil(failure.retryAfterMs / 1000)),
        retryable: true,
        retryAfterMs: failure.retryAfterMs,
        field: null
      };
    case 'upstreamFailed':
      return view(ACTION_COPY.upstreamFailed, true);
    case 'rejected':
      return view(ACTION_COPY.rejected, false);
    case 'sourceUnavailable':
      return view(ACTION_COPY.sourceUnavailable, true);
    case 'network':
      return view(ACTION_COPY.network, true);
    case 'serverError':
      return view(ACTION_COPY.generic, true);
    default:
      return view(ACTION_COPY.generic, false);
  }
};
